//////////////////
// IMPORTS
//////////////////
import * as ISchema from "#webapp/src/__schema__";
import { CalendarProfileRenderer } from "#webapp/src/components/calendar-profile";
import { ChromeFatalError } from "#webapp/src/components/chrome-fatal-error";
import { ChromeLogin } from "#webapp/src/components/chrome-login";
import { ChromeMenuDrawer } from "#webapp/src/components/chrome-menu-drawer";
import { ChromeMismatch } from "#webapp/src/components/chrome-mismatch";
import { ChromeNav } from "#webapp/src/components/chrome-nav";
import { ChromePersonalCalendar } from "#webapp/src/components/chrome-personal-calendar";
import { ChromeWelcome } from "#webapp/src/components/chrome-welcome";
import { EventRenderer } from "#webapp/src/components/event/EventRenderer";
import { FastCrawlErrorCard } from "#webapp/src/components/fast-crawl-error-card";
import { InviteModalMode } from "#webapp/src/components/invite-modal";
import { PricingDialog } from "#webapp/src/components/pricing-dialog";
import SnackBarExtListener from "#webapp/src/components/snack-bar-ext-listener/SnackBarExtListener";
import { TeamHealthDashboardRenderer } from "#webapp/src/components/team-health-dashboard";
import { TutorialBox } from "#webapp/src/components/tutorial-box";
import UseMutationExtListener from "#webapp/src/components/use-mutation-ext-listener/UseMutationExtListener";
import UseQueryExtListener from "#webapp/src/components/use-query-ext-listener/UseQueryExtListener";
import { FlowStateProvider } from "#webapp/src/hooks/useFlowState";
import {
  preSetEventFlexibility,
  saveTeamUserSettings,
  updateFlagsForUser,
  updateSetting,
} from "#webapp/src/mutations";
import {
  IOnboardingAction,
  TFastCrawlFlowState,
} from "#webapp/src/state/actions/onboarding.actions";
import { cwEcosystem, jwt, lastSignInDate } from "#webapp/src/state/local-storage";
import {
  getFastCrawlFlowState,
  getOnboardingFlowState,
  getWebOnboardingFlowState,
} from "#webapp/src/state/onboarding";
import { getReduxStore } from "#webapp/src/state/redux-store";
import {
  updateEventParentBaseWrappers,
  updateFlowStateSubscription,
} from "#webapp/src/subscriptions";
import { PageEvents, TrackingEvents, group, page, track } from "#webapp/src/util/analytics.util";
import { ChromeAppWrapperView, ChromeViewStack } from "#webapp/src/util/chrome-extension.util";
import { getValue } from "#webapp/src/util/errorable.util";
import {
  eventHandlerFeatureGridRefetch,
  refetchFeatureGridData,
} from "#webapp/src/util/eventHandlerFeatureGridRefetch";
import { getBulkExperiments } from "#webapp/src/util/experiment.util";
import { getReduxFeatureGridFromOrg } from "#webapp/src/util/feature-grid.util";
import {
  FlagNamespaces,
  UserFlags,
  isFlagSet,
  validateFlagsErrorable,
} from "#webapp/src/util/flags.util";
import { fromGlobalId } from "#webapp/src/util/graphql.util";
import { logger } from "#webapp/src/util/logger.util";
import { chromeExtensionInstalledSetting } from "#webapp/src/util/org-settings.util";
import { getCurrentOrg, getOrgCrawlFailure } from "#webapp/src/util/org.util";
import { PostMessageManager } from "#webapp/src/util/post-message.util";
import { ApolloQueryResult, OperationVariables } from "@apollo/client";
import { getApiUrl } from "@clockwise/client-commons/src/config/api";
import { paths } from "@clockwise/client-commons/src/constants/site";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";
import { isImpersonated } from "@clockwise/client-commons/src/util/jwt";
import { translate } from "@clockwise/client-commons/src/util/translations";
import { PreEventFlexibility } from "@clockwise/schema";
import { UserFeatureGridQueryQuery } from "@clockwise/web-commons/src/feature-grid/__generated__/UserFeatureGridQuery.generated";
import { PhosphorProvider } from "@clockwise/web-commons/src/icons/phosphor";
import { greys, palette } from "@clockwise/web-commons/src/styles/color.styles";
import { ExperimentsProvider } from "@clockwise/web-commons/src/util/ExperimentsContext";
import { FlagsProvider } from "@clockwise/web-commons/src/util/FlagsContext";
import { showUberCantConnect } from "@clockwise/web-commons/src/util/browser.util";
import { EcosystemProvider } from "@clockwise/web-commons/src/util/ecosystem";
import { isValidJson } from "@clockwise/web-commons/src/util/json.util";
import {
  ChromeIdleThreshold,
  QueryStringParams,
} from "@clockwise/web-commons/src/util/post-message-common.util";
import { lastChromeWrapperMountTimestamp } from "@clockwise/web-commons/src/util/session-storage";
import {
  ZonedMoment,
  getRenderTimeZone,
  setCurrentTimeZone,
  setRenderTimeZone,
} from "@clockwise/web-commons/src/util/time-zone.util";
import { compact } from "lodash";
import { DateTime } from "luxon";
import React from "react";
import toast from "react-hot-toast";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { Disposable } from "relay-runtime";
import { MonetizationContext } from "../../hooks/useMonetization";
import { FloatingScheduleButton } from "../floating-schedule-button";
import { InviteModal } from "../invite-modal";
import { NoRouteView } from "../no-route-view";
import { PlannerContext } from "../web-app-calendar/Context";
import * as styles from "./ChromeWrapper.styles";
import { IProps as IPropsChromeWrapper, IState } from "./ChromeWrapperTypes";
import { RemoveGcalHoldContainer } from "./RemoveGcalHold.container";
//////////////////
// COMPONENT
//////////////////

const CLOCKWISE_HISTORY_LOCATION_HASH = "history";

type IProps = IPropsChromeWrapper & {
  // From ChromeWrapper
  authed: boolean;
};

type IPropsMonetization = IProps & {
  refetchMonetizationFeatureGrid?: (
    variables?: Partial<OperationVariables> | undefined,
  ) => Promise<ApolloQueryResult<UserFeatureGridQueryQuery>>;
};

export const DeprecatedChromeWrapper = (props: IProps) => {
  return (
    <MonetizationContext.Consumer>
      {(context) => {
        return (
          <DeprecatedChromeWrapperComponent
            {...props}
            refetchMonetizationFeatureGrid={context?.refetchFeatureGrid}
          />
        );
      }}
    </MonetizationContext.Consumer>
  );
};

class DeprecatedChromeWrapperComponent extends React.Component<IPropsMonetization, IState> {
  private idleInterval: ReturnType<typeof setInterval>;
  private refetchFeedInterval: ReturnType<typeof setInterval>;
  private webappUpdateInterval: ReturnType<typeof setInterval>;
  private closedOrOpenInterval: ReturnType<typeof setInterval>;
  private hovered = false;
  private chromeAppWrapperLastActiveMillis = 0;
  private previousView: ChromeAppWrapperView | null; // only used to apply styles, can be outside state
  private epwDisposable?: Disposable;
  private fastCrawlDisposable?: Disposable;
  private eventWeekDisposable?: Disposable;
  private skipNextMatch = false;
  private hasSentShowTeamTip = false;

  // must track locally since both subscription and poller can return state
  // before our update to redux has caused a componentWillReceiveProps
  private didFinishRefetch = false;
  private didTeammatesRefetch = false;
  private didUserRefetch = false;
  private featureGridRefetchEventHandlers: Record<string, () => void> = {};

  constructor(props: IProps) {
    super(props);

    // pop up onboarding splash if we are fast crawling and need onboarding slides
    const org = getCurrentOrg(this.props.viewer);

    // set state
    this.state = {
      createEventAnnotation: "",
      createEventAllowedStart: null,
      createEventAllowedEnd: null,
      idle: false,
      detectedOnboardingFinished: true,
    };

    if (org) {
      PostMessageManager.sendOrgDomains(org.domains);
    }

    this.maybeSetOnboardingFlowStateFromProps(props);
    this.maybeSetFastCrawlFlowStateFromProps(props);
    this.featureGridRefetchEventHandlers = eventHandlerFeatureGridRefetch({
      onHover: this.onMouseMove,
      orgId: org?.id,
      setFeatureGridStatesFromOrg: this.setFeatureGridStatesFromOrg,
    });
  }

  private getFlowState = (viewer: ISchema.IViewer) => {
    if (!viewer || !viewer?.user) {
      return null;
    }
    const webOnboardingState = getWebOnboardingFlowState(viewer.user);
    const deprecatedOnboardingState = getOnboardingFlowState(viewer.user);

    // If user went through old onboarding at any point, that superceeds the newer web onboarding
    if (deprecatedOnboardingState && deprecatedOnboardingState.current.state === "Finished") {
      return deprecatedOnboardingState;
    }
    return webOnboardingState;
  };

  private maybeSetOnboardingFlowStateFromProps = async (props: IProps) => {
    const onboardingFlowState = this.getFlowState(props.viewer);
    if (!onboardingFlowState) {
      return;
    }

    // update state if diverging from current props
    if (onboardingFlowState.current.state !== this.props.onboardingFlowStateCurrentStatus) {
      this.props.setOnboardingFlowState(onboardingFlowState);
      PostMessageManager.sendOnboardingFlowStateCurrentStatus(onboardingFlowState.current.state);

      // If onboarding is finished, refetch feature grid
      if (onboardingFlowState.current.state === "Finished") {
        this.setState({ detectedOnboardingFinished: true });
        this.refetchAndSetFeatureGrid(this.props);
        if (this.props.refetchMonetizationFeatureGrid) {
          this.props.refetchMonetizationFeatureGrid();
        }
      } else {
        if (this.state.detectedOnboardingFinished) {
          this.setState({ detectedOnboardingFinished: false });
        }
      }
    }
  };

  private refetchAndSetFeatureGrid = async (props: IProps) => {
    const org = getCurrentOrg(props.viewer);
    if (org) {
      // refetch feature grid and put into org
      const updatedFeatureGrid = await refetchFeatureGridData(org.id);
      if (updatedFeatureGrid) {
        this.setFeatureGridStatesFromOrg(updatedFeatureGrid);
      }
    }
  };

  private maybeSetFastCrawlFlowStateFromProps = (props: IProps) => {
    const org = getCurrentOrg(props.viewer);
    const fastCrawlFlowState = org && getFastCrawlFlowState(org);
    if (!fastCrawlFlowState) {
      return;
    }

    this.maybeSetFastCrawlFlowState(fastCrawlFlowState);
  };

  private maybeSetFastCrawlFlowState = (
    fastCrawlFlowState?: ISchema.IFlowState | null,
    error?: Error,
  ) => {
    if (error) {
      logger.error("Error polling flow state", error);
    }

    if (!fastCrawlFlowState) {
      return;
    }

    // only update the flow state on new state or new error
    if (
      fastCrawlFlowState.current.state !== this.props.fastCrawlFlowStateCurrentStatus ||
      fastCrawlFlowState.current.errorMessage !== this.props.fastCrawlFlowStateErrorMessage
    ) {
      this.props.setFastCrawlFlowState(fastCrawlFlowState as TFastCrawlFlowState);
      PostMessageManager.sendFastCrawlFlowStateCurrentStatus(fastCrawlFlowState.current.state);
    }

    // refetch if its needed (always check later statuses first to avoid multiple refetches)
    if (
      this.props.fastCrawlFinished &&
      !this.props.fastCrawlFinishedRefetchComplete &&
      !this.didFinishRefetch
    ) {
      // Now setting the redux state on callback, as the redux props we were depending on were inaccurate and resulting in bad data
      // Tom mentioned this may cause refetching again if this function gets called before we complete, so i guess keep an eye on that?
      this.didFinishRefetch = true;
      this.fastCrawlRefetch(this.props.setFastCrawlFinishedRefetchComplete);
    } else if (
      this.props.fastCrawlTeammatesIdentifiedComplete &&
      !this.props.fastCrawlTeammatesIdentifiedRefetchComplete &&
      !this.didTeammatesRefetch
    ) {
      this.didTeammatesRefetch = true;
      this.fastCrawlRefetch(this.props.setFastCrawlTeammatesIdentifiedRefetchComplete);
    } else if (
      this.props.fastCrawlUserCrawledComplete &&
      !this.props.fastCrawlUserCrawledRefetchComplete &&
      !this.didUserRefetch
    ) {
      this.didUserRefetch = true;
      this.fastCrawlRefetch(this.props.setFastCrawlUserCrawledRefetchComplete);
    }

    // start or stop subscription?
    if (
      this.fastCrawlDisposable &&
      this.props.fastCrawlFinished &&
      !this.props.fastCrawlFlowStateErrorMessage
    ) {
      this.fastCrawlDisposable.dispose();
      delete this.fastCrawlDisposable;
    } else if (
      !this.fastCrawlDisposable &&
      (!this.props.fastCrawlFinished || this.props.fastCrawlFlowStateErrorMessage)
    ) {
      this.subscribeFastCrawl();
    }
  };

  private setOrgBillingGroupIdsFromOrg = (org: ISchema.IOrg) => {
    const orgBillingGroupsFromOrg = getValue(org?.billingGroups);
    if (orgBillingGroupsFromOrg?.list) {
      const idsOfOrgBillingGroups = orgBillingGroupsFromOrg.list
        .map((bg) => bg?.id || "")
        .filter((id) => Boolean(id));
      this.props.setOrgBillingGroupIds(idsOfOrgBillingGroups);
    }
  };

  private setFeatureGridStatesFromOrg = (org: ISchema.IOrg) => {
    const featureGrid = getReduxFeatureGridFromOrg(org);
    if (featureGrid) {
      this.props.setFeatureGridStates(featureGrid);
    }
  };

  private maybeShowTeamCalendarTip = (props: IProps) => {
    const { viewer } = props;

    const org = getCurrentOrg(viewer);
    const onboardingFlowState = this.getFlowState(props.viewer);
    if (org && onboardingFlowState && onboardingFlowState.current.state === "Finished") {
      const flagsErrorable =
        props.viewer && props.viewer.user && props.viewer.user.chromeExtensionFlagsErrorable;

      const userTeams =
        (org.userTeams &&
          org.userTeams.__typename !== "GraphEntityError" &&
          org.userTeams.list.length &&
          org.userTeams.list) ||
        [];

      const primaryTeam =
        org.primaryTeam && org.primaryTeam.__typename !== "GraphEntityError" && org.primaryTeam;

      const matchedUserTeam =
        userTeams.find(
          (team) =>
            team.userSettings &&
            team.userSettings.__typename !== "GraphEntityError" &&
            team.userSettings.useTeamCalendar &&
            team.settings &&
            team.settings.__typename !== "GraphEntityError" &&
            team.settings.manageTeamCalendar &&
            !!(primaryTeam && team.teamId === primaryTeam.teamId && team.teamCalendarId),
        ) ||
        userTeams.find((team) => !!team.teamCalendarId) ||
        null;

      const shouldShowCalendarTip =
        matchedUserTeam &&
        matchedUserTeam.teamCalendarId &&
        !isFlagSet(UserFlags.Onboarding.TeamCalendar.name, flagsErrorable) &&
        !this.hasSentShowTeamTip;

      if (shouldShowCalendarTip) {
        PostMessageManager.showTeamCalendarTip(matchedUserTeam.teamCalendarId);
        this.hasSentShowTeamTip = true;
      }
    }
  };

  // ~-~-~-~-~-~-~-
  // Life-Cycle
  // ~-~-~-~-~-~-~-
  private syncRedux(props: IProps) {
    const org = getCurrentOrg(props.viewer);

    if (!props.match || this.skipNextMatch || !org) {
      this.skipNextMatch = false;
      return;
    }

    const { params } = props.match;
    const { pathname, hash } = props.match.location;

    if (pathname.startsWith(`${paths.webApp}/calendar-profile`)) {
      props.setActiveViewCalendarProfile(atob(params.calendarId), params.viewWeek);
    } else if (pathname.startsWith(`${paths.webApp}/team-analytics`)) {
      props.setActiveView(ChromeAppWrapperView.TeamHealth);
    } else if (pathname.startsWith(`${paths.webApp}/event`)) {
      try {
        const externalEventId = atob(params.encodedExternalEventId);
        const calendarId =
          (params.fullView && params.fullView !== "f"
            ? atob(params.fullView)
            : params.encodedCalendarId &&
              params.encodedCalendarId !== "f" &&
              atob(params.encodedCalendarId)) || org.primaryOrgCalendar;

        const showClockwiseHistory = !!hash?.includes(CLOCKWISE_HISTORY_LOCATION_HASH);

        props.setActiveViewEvent(
          externalEventId,
          calendarId,
          false,
          undefined,
          showClockwiseHistory,
        );
      } catch (e) {
        // do nothing if atob fails
      }
    } else if (pathname.startsWith(`${paths.webApp}/calendar`)) {
      props.setActiveView(ChromeAppWrapperView.Day);
    }
  }

  private syncLocation(props: IProps) {
    const org = getCurrentOrg(props.viewer);

    if (!props.router || !props.match || !org) {
      return;
    }

    switch (props.activeView) {
      case ChromeAppWrapperView.CalendarProfile:
        const { calendarProfile } = props;

        props.router.push(
          `${paths.webApp}/calendar-profile/${btoa(calendarProfile.calendarId)}${
            calendarProfile.viewWeek ? `/${calendarProfile.viewWeek}` : ""
          }${props.match.location.hash}`,
        );
        break;
      case ChromeAppWrapperView.TeamHealth:
        props.router.push(`${paths.webApp}/team-analytics${props.match.location.hash}`);
        break;
      case ChromeAppWrapperView.Event:
        const { calendarId, externalEventId, showClockwiseHistory } = props.activeViewEvent;

        const maybeCalendarId = calendarId !== org.primaryOrgCalendar ? `${btoa(calendarId)}` : "";

        let locationHash = props.match.location.hash;
        if (!showClockwiseHistory) {
          locationHash = locationHash
            .split("#/")
            .filter((hash) => hash !== CLOCKWISE_HISTORY_LOCATION_HASH)
            .join("#/");
        }

        const url = compact([paths.webApp, "event", btoa(externalEventId), maybeCalendarId]).join(
          "/",
        );
        props.router.push(`${url}${locationHash}`);

        break;
      case ChromeAppWrapperView.Day:
        props.router.push(`${paths.webApp}/calendar${props.match.location.hash}`);
        break;
      default:
        props.router.push(`${paths.webApp}${props.match.location.hash}`);
        break;
    }

    this.skipNextMatch = true;
  }

  private hasViewStateChanged(nextProps: IProps) {
    switch (nextProps.activeView) {
      case ChromeAppWrapperView.CalendarProfile:
        return (
          this.props.calendarProfile.calendarId !== nextProps.calendarProfile.calendarId ||
          this.props.calendarProfile.viewWeek !== nextProps.calendarProfile.viewWeek
        );
      case ChromeAppWrapperView.Event:
        return (
          this.props.activeViewEvent.externalEventId !== nextProps.activeViewEvent.externalEventId
        );

      default:
        return false;
    }
  }

  public UNSAFE_componentWillMount() {
    // if chrome extension is installed, send setting to services
    const org = getCurrentOrg(this.props.viewer);
    if (org && !chromeExtensionInstalledSetting.isOn(org) && this.props.chromeExtensionInstalled) {
      const setting = chromeExtensionInstalledSetting.get(org);
      const value = ["true"];

      if (setting) {
        updateSetting(this.props.relay.environment, { setting, value }, () => "no-op", logger.warn);
      }
    }

    this.syncRedux(this.props);
  }

  public componentDidMount() {
    page(PageEvents.CHROME_EXTENSION);
    if (!lastSignInDate.get() && this.props.authed) {
      // If signed in and this is not set, set it.
      lastSignInDate.set(DateTime.now().toISO());
    }
    this.startIdlePoll();
    this.startWebappUpdatePoll();
    this.startRefetchPoll();
    this.startClosedOrOpenPoll();

    const org = getCurrentOrg(this.props.viewer);

    if (org && org.id) {
      // analytics
      group(`Org::${fromGlobalId(org.id).id}`, {
        name: org.name,
        created: new ZonedMoment(org.createdTime),
      });

      if (org.experiments) {
        this.props.setExperiments(org.experiments);
      } else {
        logger.warn("User has org object but no experiments");
      }

      // sync org with chrome-extension
      PostMessageManager.syncActiveOrg(org);

      if (
        org.eventColoringSettingsErrorable &&
        org.eventColoringSettingsErrorable.__typename === "EventColoringSettings"
      ) {
        PostMessageManager.sendColorSettings(org.eventColoringSettingsErrorable);
      }

      PostMessageManager.sendUser(this.props.viewer.user);

      this.handleOpenLinkShareModal();

      this.sendUserGoalsToExtension(org);

      lastChromeWrapperMountTimestamp.set(DateTime.now());

      this.setFeatureGridStatesFromOrg(org);

      this.setOrgBillingGroupIdsFromOrg(org);
    }

    this.maybeShowTeamCalendarTip(this.props);

    this.featureGridRefetchEventHandlers.initListeners();

    // subscribe
    if (this.props.activeView === ChromeAppWrapperView.Day) {
      this.subscribeEpw();
    }

    const flagsErrorable =
      this.props.viewer &&
      this.props.viewer.user &&
      this.props.viewer.user.chromeExtensionFlagsErrorable;

    if (
      this.props.isChromeExtension &&
      flagsErrorable &&
      validateFlagsErrorable(flagsErrorable) &&
      !isFlagSet(UserFlags.Onboarding.HasInstalledChromeExtension.name, flagsErrorable)
    ) {
      updateFlagsForUser(
        this.props.relay.environment,
        {
          flagNamespace: FlagNamespaces.ChromeExtension,
          flagNames: [UserFlags.Onboarding.HasInstalledChromeExtension.name],
          enable: true,
        },
        () => null,
        () => null,
      );
    }

    // detect sudo and timezone change
    if (
      isImpersonated(jwt.get()) &&
      org &&
      org.userWorkingHours &&
      org.userWorkingHours.workingHours.timeZone &&
      (getRenderTimeZone() !== org.userWorkingHours.workingHours.timeZone ||
        getRenderTimeZone() !== org.userWorkingHours.workingHours.timeZone)
    ) {
      window.alert(`Changing time zone to ${org.userWorkingHours.workingHours.timeZone}`);
      setRenderTimeZone(org.userWorkingHours.workingHours.timeZone);
      setCurrentTimeZone(org.userWorkingHours.workingHours.timeZone);
    }
  }

  public componentWillUnmount() {
    clearInterval(this.idleInterval);
    clearInterval(this.refetchFeedInterval);
    clearInterval(this.closedOrOpenInterval);
    clearInterval(this.webappUpdateInterval);

    this.featureGridRefetchEventHandlers.removeListeners();

    if (this.epwDisposable) {
      this.epwDisposable.dispose();
      delete this.epwDisposable;
    }

    if (this.fastCrawlDisposable) {
      this.fastCrawlDisposable.dispose();
      delete this.fastCrawlDisposable;
    }

    if (this.eventWeekDisposable) {
      this.eventWeekDisposable.dispose();
      delete this.eventWeekDisposable;
    }
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps.selectedMoment !== this.props.selectedMoment) {
      this.subscribeEpw();
    } else if (!this.epwDisposable && this.props.activeView === ChromeAppWrapperView.Day) {
      this.subscribeEpw();
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const { preEventFlexibility, preEventKey, preEventCalendarId } = nextProps;
    const nextOrg = getCurrentOrg(nextProps.viewer);
    const org = getCurrentOrg(this.props.viewer);

    this.maybeShowTeamCalendarTip(nextProps);

    // update flow states
    this.maybeSetFastCrawlFlowStateFromProps(nextProps);

    if (nextOrg && nextOrg !== org) {
      this.maybeSetOnboardingFlowStateFromProps(nextProps);
      this.sendUserGoalsToExtension(nextOrg);

      if (
        nextOrg.eventColoringSettingsErrorable &&
        nextOrg.eventColoringSettingsErrorable.__typename === "EventColoringSettings"
      ) {
        PostMessageManager.sendColorSettings(nextOrg.eventColoringSettingsErrorable);
      }
    }

    // maybe sync active org with chrome-extension
    if (nextOrg && !this.props.shouldSyncActiveOrg && nextProps.shouldSyncActiveOrg) {
      PostMessageManager.syncActiveOrg(nextOrg);
      this.props.setShouldSyncActiveOrg(false);
    }

    //  detect onboardingUpdates
    if (
      nextProps.onboardingFlowStateCurrentStatus !== this.props.onboardingFlowStateCurrentStatus
    ) {
      PostMessageManager.sendOnboardingFlowStateCurrentStatus(
        nextProps.onboardingFlowStateCurrentStatus,
      );
      if (
        this.state.detectedOnboardingFinished &&
        nextProps.onboardingFlowStateCurrentStatus !== "Finished"
      ) {
        this.setState({ detectedOnboardingFinished: false });
      }

      // If onboarding is finished, refetch feature grid
      if (nextProps.onboardingFlowStateCurrentStatus === "Finished") {
        this.setState({ detectedOnboardingFinished: true });
        this.refetchAndSetFeatureGrid(nextProps);
        if (this.props.refetchMonetizationFeatureGrid) {
          this.props.refetchMonetizationFeatureGrid();
        }
        this.forceUpdate();
      }
    }

    // detect event flexibility on event creation
    if (
      nextOrg?.id &&
      preEventFlexibility !== this.props.preEventFlexibility &&
      preEventKey !== this.props.preEventKey
    ) {
      if (preEventKey === null || preEventFlexibility === null) {
        return;
      }

      preSetEventFlexibility(
        this.props.relay.environment,
        nextOrg.id,
        preEventCalendarId,
        preEventKey,
        preEventFlexibility,
        (res) => {
          this.onPreSetEventFlexibilitySuccess(preEventFlexibility, res);
        },
        (err) => {
          this.onPreSetEventFlexibilityFailure(err);
        },
      );
    }

    // detect set user flag for inline autopilot and show day of week
    if (nextProps.setUserFlagMillis > this.props.setUserFlagMillis) {
      updateFlagsForUser(
        this.props.relay.environment,
        {
          // TODO: we probably shouldnt be hardcoding the namespace, but this post-message only will ever come from the chrome extension
          flagNamespace: FlagNamespaces.ChromeExtension,
          flagNames: [nextProps.userFlag],
          enable: true,
        },
        () => {
          if (nextProps.userFlag === UserFlags.Onboarding.HasSeenDayOfWeek.name) {
            track(TrackingEvents.FLEXIBLE_MEETING.ADJUST_DATE_RANGE.AUTO_EXPAND, {
              surface: "SchedulingModal",
            });
          }
        },
        () => logger.error("Failed to set user flag"),
      );
    }

    // detect calendar unsubscribed for team settings
    if (
      nextOrg &&
      nextProps.unsubscribedCalendarIdMillis > this.props.unsubscribedCalendarIdMillis
    ) {
      const matchedTeam =
        nextOrg.userTeams &&
        nextOrg.userTeams.__typename !== "GraphEntityError" &&
        nextOrg.userTeams.list.find(
          (team) => team.teamCalendarId === nextProps.unsubscribedCalendarId,
        );

      if (matchedTeam) {
        saveTeamUserSettings(
          nextProps.relay.environment,
          { orgRelayId: nextOrg.id, teamId: matchedTeam.teamId, useTeamCalendar: false },
          () => null,
          () => null,
        );

        track(TrackingEvents.TEAMS.TEAM_SETTINGS_USER_UNSUBSCRIBED_TEAM_CALENDAR, {
          unsubscribedCalendarId: matchedTeam.teamCalendarId,
        });
      }
    }

    // detect chrome menu button click
    if (nextProps.menuDrawerOpen && !this.props.menuDrawerOpen) {
      track(TrackingEvents.CHROME_MENU.CHROME_MENU_DRAWER_OPEN);
    }

    // detect extension open/closed
    if (nextProps.chromeExtensionOpen !== this.props.chromeExtensionOpen) {
      if (nextProps.chromeExtensionOpen) {
        track(TrackingEvents.CHROME_EXTENSION.OPEN, {
          showingUberCantConnect: showUberCantConnect(this.props.windowUserEmail),
          unauthedCalendarId: this.props.windowUserEmail,
        });
      } else {
        track(TrackingEvents.CHROME_EXTENSION.CLOSED, {
          showingUberCantConnect: showUberCantConnect(this.props.windowUserEmail),
          unauthedCalendarId: this.props.windowUserEmail,
        });
      }
    }

    // detect pricing dialog open call from chrome extension
    if (
      nextProps.pricingDialogInfo.openPricingDialogMillis >
      this.props.pricingDialogInfo.openPricingDialogMillis
    ) {
      this.props.setPricingDialogOpen(true, undefined, this.props.pricingDialogInfo.initialView);
    }

    if (this.props.activeView !== nextProps.activeView || this.hasViewStateChanged(nextProps)) {
      this.syncLocation(nextProps);
    } else if (
      this.props.match &&
      nextProps.match &&
      this.props.match.location.pathname !== nextProps.match.location.pathname
    ) {
      this.syncRedux(nextProps);
    }

    if (
      !this.props.shouldRefetchDueToPlanUpgrade &&
      nextProps.shouldRefetchDueToPlanUpgrade &&
      nextOrg
    ) {
      this.refetchAndSetFeatureGrid(nextProps);
      this.props.updateShouldRefetch(false);
    }
  }

  private handleOpenLinkShareModal = () => {
    const url =
      this.props.mode === "extension" ? this.props.calendarUrl || "" : window.location.href;
    const search = url.split("?")[1];
    const searchParams = new URLSearchParams(search);

    if (searchParams.has(QueryStringParams.ShareSchedulingLink)) {
      // if we are not open, open
      if (!this.props.chromeExtensionOpen) {
        PostMessageManager.toggleSidebar(true, false);
      }
      setTimeout(() => {
        this.props.setOpenShareLinkModal(true);
        PostMessageManager.clearQueryParams(QueryStringParams.ShareSchedulingLink);
      }, 500);
    }
  };

  private sendUserGoalsToExtension(org: ISchema.IOrg) {
    if (!org) {
      return;
    }

    let focusTimePerWeek = 0;

    if (
      org.shadowCalendarSettingsErrorable?.__typename === "ShadowCalendarSettings" &&
      org.shadowCalendarSettingsErrorable?.focusTimeSyncEnabled
    ) {
      if (org.shadowCalendarSettingsErrorable.focusTimeSyncAll) {
        focusTimePerWeek = -1; // Chrome Extension knows how to consume this.
      } else {
        focusTimePerWeek = org.shadowCalendarSettingsErrorable.focusTimeIdealMinutesPerWeek || 0;
      }
    }

    // Appending Focus Time goal, which is for some reason stored elsewhere in shadowCalendarSettings, into userGoals
    // for the sake of simplifying communication with the Chrome Extension.
    // Syncall "All my available Focus Time" -> -1
    // No focus time scheduling -> 0
    // Max number of hours per week -> number greater than 0
    PostMessageManager.sendUserGoals(
      org?.userGoals?.edges?.map((goalEdge) => goalEdge.node) || [],
      focusTimePerWeek,
    );
  }

  private subscribeEpw = () => {
    const org = getCurrentOrg(this.props.viewer);

    if (!org) {
      return;
    }

    if (this.epwDisposable) {
      this.epwDisposable.dispose();
      delete this.epwDisposable;
    }

    const weekStart = this.props.selectedMoment.clone().startOf("week");

    const input: ISchema.IUpdateEventParentBaseWrappersSubscriptionInput = {
      orgRelayId: org.id,
      calendarIds: org.primaryOrgCalendar ? [org.primaryOrgCalendar] : [],
      dates: new Array(7)
        .fill(null)
        .map((_d, i) => weekStart.clone().add(i, "days").format("YYYY-MM-DD")),
      timeZone: this.props.selectedMoment.timeZone,
      includeAllDay: true,
    };

    this.epwDisposable = updateEventParentBaseWrappers(this.props.relay.environment, input);
  };

  private subscribeFastCrawl = () => {
    const { relay, viewer } = this.props;
    const org = getCurrentOrg(viewer);
    const flowState = org && getFastCrawlFlowState(org);

    if (this.fastCrawlDisposable) {
      return;
    }

    const flowStateRelayId = flowState && flowState.id;

    if (flowStateRelayId) {
      this.fastCrawlDisposable = updateFlowStateSubscription(relay.environment, {
        flowStateRelayId,
      });
    } else {
      logger.warn("failed to subscribe to fast crawl updates due to missing user or flowstate id");
    }
  };

  private fastCrawlRefetch = (refetchCallback: () => IOnboardingAction) => {
    // modern
    this.props.relay.refetch(
      {},
      {},
      () => {
        refetchCallback();
      },
      { force: true },
    );
  };

  private checkWebappUpdate = () => {
    // fetch latest update timestamps
    if (window.fetch && this.props.mode !== "webapp") {
      window.fetch(`${getApiUrl()}/update`, { credentials: "include" }).then((response: any) => {
        response.text().then((text: string) => {
          const parsed = (isValidJson(text) && JSON.parse(text)) || null;
          if (parsed && parsed.chromeAppUpdateTimes) {
            PostMessageManager.sendWebappUpdate(parsed.chromeAppUpdateTimes);
          }
        });
      });
    } else if (this.props.mode !== "webapp") {
      logger.error("Failed to fetch /update, fetch not found on window");
    }
  };

  private onPreSetEventFlexibilitySuccess = (
    preEventFlexibility: PreEventFlexibility,
    response?: any,
  ) => {
    if (response?.preSetEventFlexibility?.successful) {
      toast.success("Set flexibility preferences!");

      track(TrackingEvents.FLEXIBLE_MEETING.SET_FLEXIBLE_ON_CREATE, {
        preEventFlexibility,
      });
    }
  };

  private onPreSetEventFlexibilityFailure = (err: any) => {
    toast.error("Failed to save flexibility preferences");
    logger.error("Unable to save preSetEventFlexibility", err);
  };

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private startWebappUpdatePoll = () => {
    this.checkWebappUpdate();
    this.webappUpdateInterval = setInterval(this.checkWebappUpdate, 3600000); // 60 minutes (avoid everyone at once)
  };
  private startIdlePoll = () => {
    this.idleInterval = setInterval(this.detectIdle, 1000);
  };

  private startRefetchPoll = () => {
    // 15 minutes = 900,000 millis
    this.refetchFeedInterval = setInterval(this.props.refetchFeed, 900000);
  };

  private startClosedOrOpenPoll = () => {
    // 60 minutes = 3,600,000
    this.trackClosedOrOpen();
    this.closedOrOpenInterval = setInterval(this.trackClosedOrOpen, 3600000);
  };

  private trackClosedOrOpen = () => {
    // Given how frequently this can fire, this is a very heavy tracking event that tracks whether or not a browser with
    // the extension installed and enabled has the extension open, user signed in or not, every 60 minutes.
    // Must enable with an endDate passed into track() to very deliberately enable and then disable.
    const endDate = new Date("2021-12-22");
    const trackOpenClosed = this.props.chromeExtensionOpen
      ? TrackingEvents.CHROME_EXTENSION.POLL_IS_OPEN
      : TrackingEvents.CHROME_EXTENSION.POLL_IS_CLOSED;
    track(trackOpenClosed, undefined, endDate);
  };

  private detectIdle = () => {
    const now = new Date().valueOf();
    const lastMillis = this.chromeAppWrapperLastActiveMillis;
    // if our state isn't idle but both the component and extension are idle, change state
    if (
      !this.state.idle &&
      now - lastMillis > ChromeIdleThreshold &&
      this.props.chromeExtensionIdle
    ) {
      this.setState({ idle: true });
      // When the calendarUrl has an event id we want to keep showing the view of the event
      // Thus skip changing the active view when the event view was set by a URL param
      const {
        activeViewEvent: { currentViewOfEventWasSetByUrlParam },
      } = getReduxStore().getState().webExtension;
      if (!this.hovered && !currentViewOfEventWasSetByUrlParam) {
        this.props.setActiveView(this.props.selectedTabView);
      }
    } else if (
      this.state.idle &&
      (now - lastMillis <= ChromeIdleThreshold || !this.props.chromeExtensionIdle)
    ) {
      this.setState({ idle: false });
    }
  };

  private onMouseMove = (hovered: boolean, event?: React.MouseEvent<HTMLDivElement>) => {
    const now = new Date().valueOf();
    this.chromeAppWrapperLastActiveMillis = now; // store locally to prevent too many renders
    const isHovered =
      (!hovered &&
        event &&
        event.relatedTarget &&
        (event.relatedTarget as Node).nodeType === Node.ELEMENT_NODE && // check if the browser returned a node or a cross-origin error obj
        document.body.contains(event.relatedTarget as HTMLElement)) ||
      hovered;
    // store current state
    this.hovered = isHovered;
    // post message
    PostMessageManager.sendWebExtensionHovered(isHovered);
  };

  private onClick = () => {
    const now = new Date().valueOf();
    PostMessageManager.sendWebExtensionClicked(now);
  };

  private onScrollContent = (event: React.UIEvent<HTMLDivElement>) => {
    this.props.setContentScrollTop(event.currentTarget.scrollTop);
  };

  private getCurrentView = () => {
    const { viewer, calendarMismatch, createEvent, windowUserEmail } = this.props;
    const { authed } = this.props;

    if (!authed) {
      return ChromeAppWrapperView.NoAuth;
    }

    const org = getCurrentOrg(viewer);

    const { personalCalendar, orgCrawlFailure } = getOrgCrawlFailure(viewer, windowUserEmail);
    const mostRecentActive = this.props.activeView;

    if (!org) {
      return ChromeAppWrapperView.NoOrg;
    } else if (calendarMismatch) {
      return ChromeAppWrapperView.CalendarMismatch;
    } else if (personalCalendar) {
      return ChromeAppWrapperView.PersonalCalendar;
    } else if (orgCrawlFailure) {
      switch (orgCrawlFailure) {
        case "GoogleContactSharingDisabled":
          return ChromeAppWrapperView.GoogleContactSharingDisabled;
        case "GoogleApiAccessDisabled":
          return ChromeAppWrapperView.GoogleApiAccessDisabled;
        case "ShouldCrawl":
          return ChromeAppWrapperView.ShouldCrawl;
        case "NoCrawl":
          return ChromeAppWrapperView.NoCrawl;
        case "NoExternalOrganization":
          return ChromeAppWrapperView.NoExternalOrganization;
        default:
          return ChromeAppWrapperView.GenericCrawlFailure;
      }
    } else if (!this.state.detectedOnboardingFinished) {
      return ChromeAppWrapperView.Onboarding;
    } else if (createEvent) {
      return ChromeAppWrapperView.CreateEvent;
    } else {
      return mostRecentActive;
    }
  };

  private getBackgroundColor = (view: ChromeAppWrapperView) => {
    switch (view) {
      case ChromeAppWrapperView.GoogleContactSharingDisabled:
      case ChromeAppWrapperView.GoogleApiAccessDisabled:
      case ChromeAppWrapperView.ShouldCrawl:
      case ChromeAppWrapperView.GenericCrawlFailure:
      case ChromeAppWrapperView.NoExternalOrganization:
      case ChromeAppWrapperView.NoCrawl:
      case ChromeAppWrapperView.NoAuth:
      case ChromeAppWrapperView.Onboarding:
      case ChromeAppWrapperView.TeamHealth:
        return greys.palePaleGrey;
      default:
        return palette.white;
    }
  };

  private getContainerStyle = (view: ChromeAppWrapperView): React.CSSProperties => {
    switch (view) {
      case ChromeAppWrapperView.Event:
        return {
          paddingLeft: 0,
          paddingRight: 0,
        };
      case ChromeAppWrapperView.Day:
        return {
          overflowY: "unset",
          paddingLeft: 0,
          paddingRight: 0,
        };
      case ChromeAppWrapperView.EventScheduler:
        return {
          overflowY: "unset",
        };
      default:
        return {};
    }
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private maybeRenderPricingDialog = (org: ISchema.IOrg | null) => {
    const { pricingDialogOpen, teamId, initialView } = this.props.pricingDialogState;

    if (!pricingDialogOpen || !org) {
      return "";
    }

    const primaryTeamId =
      (org.primaryTeam &&
        org.primaryTeam.__typename !== "GraphEntityError" &&
        org.primaryTeam.teamId) ||
      undefined;

    return (
      <PricingDialog
        org={org}
        teamId={teamId || primaryTeamId}
        initialView={initialView || "Pricing"}
      />
    );
  };

  private renderView = (view: ChromeAppWrapperView) => {
    const { viewer, fatalError } = this.props;
    const { authed } = this.props;

    // very much stop now if we have a fatal error
    if (fatalError) {
      return (
        <div className="cw-pt-3">
          <ChromeFatalError />
        </div>
      );
    }

    // stop now if we arent authed
    if (!authed) {
      if (cwEcosystem.get() === "Microsoft") {
        window.location.href = paths.m365SignIn;
      } else {
        return <ChromeLogin viewer={viewer} />;
      }
    }
    const org = getCurrentOrg(viewer);
    if (!org) {
      // note: NoOrg view handled below, but this will almost certainly happen first
      return null;
    }

    // render
    switch (view) {
      case ChromeAppWrapperView.Onboarding:
        return (
          <FlowStateProvider>
            <ChromeWelcome />
          </FlowStateProvider>
        );
      case ChromeAppWrapperView.CalendarMismatch:
        return <ChromeMismatch calendarMismatch={this.props.calendarMismatch} />;
      case ChromeAppWrapperView.PersonalCalendar:
        return <ChromePersonalCalendar user={viewer.user} org={org} />;
      case ChromeAppWrapperView.NoOrg:
      case ChromeAppWrapperView.GoogleContactSharingDisabled:
      case ChromeAppWrapperView.GoogleApiAccessDisabled:
      case ChromeAppWrapperView.ShouldCrawl:
      case ChromeAppWrapperView.NoCrawl:
      case ChromeAppWrapperView.GenericCrawlFailure:
      case ChromeAppWrapperView.NoExternalOrganization:
        return <FastCrawlErrorCard view={view} orgId={org.id} />;
      case ChromeAppWrapperView.Event:
        return (
          <PlannerContext.Provider value={{}}>
            <EventRenderer event={this.props.activeViewEvent} />
          </PlannerContext.Provider>
        );
      case ChromeAppWrapperView.TeamHealth:
        return <TeamHealthDashboardRenderer environment={this.props.relay.environment} org={org} />;
      case ChromeAppWrapperView.Day:
        return <NoRouteView />;
      case ChromeAppWrapperView.CalendarProfile:
        return <CalendarProfileRenderer environment={this.props.relay.environment} org={org} />;
      default:
        return <div />;
    }
  };

  private maybeRenderMenuDrawer = (view: ChromeAppWrapperView) => {
    if (view === ChromeAppWrapperView.NoAuth || view === ChromeAppWrapperView.NoOrg) {
      return <span />;
    }
    const { viewer, menuDrawerOpen } = this.props;

    return <ChromeMenuDrawer viewer={viewer} open={menuDrawerOpen} />;
  };

  private shouldRenderFeedScheduler = (view: ChromeAppWrapperView) => {
    switch (view) {
      case ChromeAppWrapperView.Day:
      case ChromeAppWrapperView.TeamHealth:
        return true;
      default:
        return false;
    }
  };

  private maybeRenderFAB = (view: ChromeAppWrapperView, org: ISchema.IOrg | null) => {
    const { viewer, fatalError } = this.props;

    if (!org || !viewer || !viewer.user || fatalError || !this.state.detectedOnboardingFinished) {
      return;
    }

    if (this.shouldRenderFeedScheduler(view)) {
      return <FloatingScheduleButton />;
    }
    return null;
  };

  private maybeRenderTutorialBox = (view: ChromeAppWrapperView) => {
    // if we don't have a user, we cant render the tutorial box
    if (view === ChromeAppWrapperView.NoAuth || view === ChromeAppWrapperView.NoOrg) {
      return <span />;
    }

    return <TutorialBox view={view} user={this.props.viewer.user} />;
  };

  private viewTransitionDirection = (newView: ChromeAppWrapperView) => {
    const newViewStackIndex = ChromeViewStack.indexOf(newView);
    const prevViewStackIndex =
      this.previousView === null ? -1 : ChromeViewStack.indexOf(this.previousView);

    this.previousView = newView; // IMPORTANT!

    const baseEndIndex = 2;
    const withinBase =
      newViewStackIndex >= 0 &&
      newViewStackIndex <= baseEndIndex &&
      prevViewStackIndex >= 0 &&
      prevViewStackIndex <= baseEndIndex;

    if (newViewStackIndex === -1 || prevViewStackIndex === -1 || withinBase) {
      return "none"; // no animation
    } else if (newViewStackIndex < prevViewStackIndex) {
      return "out"; // going down the stack, so current view slides out
    } else {
      return "in"; // going up the stack, so current view slides in
    }
  };

  public render() {
    const { viewer, chromeExtensionOpen, relay, authed } = this.props;
    const view = this.getCurrentView();
    const backgroundColor = this.getBackgroundColor(view);
    const org = getCurrentOrg(viewer);
    const webAppFlagsErrorable = this.props.viewer.user?.webAppFlagsErrorable;
    const defaultView = this.props.selectedTabView;
    const modernContentStyle: React.CSSProperties = {
      ...styles.modernContent,
      ...this.getContainerStyle(view),
    };
    const featureFlags = getBulkExperiments();

    // address Safari bug aparent during onboarding conflict between "overflow: hidden scroll" with a set z-index
    // issie => https://app.asana.com/0/357266512179263/1202200571716523/f
    if (view === ChromeAppWrapperView.Event) {
      modernContentStyle.zIndex = "unset";
    }

    // transition-direction
    const transitionDirection = this.viewTransitionDirection(view);

    const containerClassName =
      this.props.mode === "extension"
        ? this.props.classes.modernContainer
        : this.props.classes.modernContainerWebapp;

    return (
      <PhosphorProvider>
        <EcosystemProvider>
          <ExperimentsProvider experiments={featureFlags}>
            <FlagsProvider namespace={FlagNamespaces.Webapp} flagsErrorable={webAppFlagsErrorable}>
              <TranslationContext.Provider value={translate()}>
                <div className={`view-transition-${transitionDirection}`}>
                  <TransitionGroup>
                    <CSSTransition key={view} timeout={300} classNames="slide">
                      <div
                        className={containerClassName}
                        style={{ background: backgroundColor }}
                        onMouseLeave={(event) => this.onMouseMove(false, event)}
                        onClick={() => this.onClick()}
                      >
                        <ChromeNav
                          view={view}
                          defaultView={defaultView}
                          authed={authed}
                          environment={relay.environment}
                          org={org}
                        />
                        {this.maybeRenderTutorialBox(view)}
                        <div style={modernContentStyle} onScroll={this.onScrollContent}>
                          {this.maybeRenderFAB(view, org)}
                          {this.renderView(view)}
                        </div>
                        <div style={chromeExtensionOpen ? styles.nop : styles.closedIndicator} />
                        {this.maybeRenderMenuDrawer(view)}
                      </div>
                    </CSSTransition>
                  </TransitionGroup>
                  {this.maybeRenderPricingDialog(org)}
                  <InviteModal
                    isOpen={this.props.openShareWithCoworkersModal.open}
                    onClose={() =>
                      this.props.setOpenShareWithCoworkersModal(false, InviteModalMode.Invite)
                    }
                    mode={this.props.openShareWithCoworkersModal.mode}
                  />
                  <RemoveGcalHoldContainer orgId={org?.id} />
                  <SnackBarExtListener />
                  <UseQueryExtListener />
                  <UseMutationExtListener />
                </div>
              </TranslationContext.Provider>
            </FlagsProvider>
          </ExperimentsProvider>
        </EcosystemProvider>
      </PhosphorProvider>
    );
  }
}
